import React, { useCallback, useEffect, useState } from 'react';
import { useQuery } from '@common/hooks';
import { useCurrentEntity } from '@identity/hooks';
import { Select } from 'antd';
import { capitalize, cloneDeep, isEqual } from 'lodash';
import { Colors } from '@common/tokens';
import {
  Banner,
  Button,
  ButtonGroup,
  CurrencyInput,
  ErrorHandler,
  Gutter,
  LoadingState,
  Title,
  Divider,
  Form,
  Input,
} from '@common/components';
import OrganizationUnitSelect from 'works/legacy/components/organization-unit-select/organization-unit-select';
import { OrganizationBusinessSelect } from './_organization-business-select';
import { RoleItem } from './_role-item';
import { ComplianceListTemplateSelect } from './_compliance-list-template-select';
import { BillingConfigSelect } from './_billing-config-select';
import {
  CertificationsSelect,
  ClinicalSpecialtiesSelect,
  TechnologiesSelect,
} from '@clinician-profile/components';
import {
  GET_STAFFING_REQUEST_TEMPLATE_QUERY,
  STAFFING_REQUEST_TEMPLATE_QUERY,
} from 'works/legacy/graphql/staffing-request-template';
import {
  findEntityById,
  updateTemplateName,
} from './_staffing-request-template-form-service';
import { ENUM_QUERY } from '@common/graphql';
import { YearsOfExperienceSelect } from '@jobs/components';
import {
  formatSelectedCertifications,
  formatSelectedSpecialties,
} from '@clinician-profile/services';
import {
  CertificationType,
  SpecialtyType,
  FormValuesType,
} from './_staffing-request-template-form-types';

export function StaffingRequestTemplateForm({
  open,
  handleSubmit,
  staffingRequestTemplateId = '',
  action,
  onClose,
}: {
  open: boolean;
  handleSubmit: (
    values: FormValuesType,
    selectedSpecialties: SpecialtyType[],
    selectedCertifications: CertificationType[]
  ) => void;
  staffingRequestTemplateId?: string;
  action: 'edit' | 'create' | 'clone';
  onClose: () => void;
}) {
  const isEdit = action === 'edit';

  const [form] = Form.useForm<FormValuesType>();

  useEffect(() => {
    form.resetFields();
  }, [open, form]);

  const [selectedSpecialties, setSelectedSpecialties] = useState<
    SpecialtyType[]
  >([]);
  const [selectedCertifications, setSelectedCertifications] = useState<
    CertificationType[]
  >([]);
  const [name, setName] = useState<string | null>(null);
  const [disableSubmitButton, setDisableSubmitButton] =
    useState<boolean>(isEdit);
  const [initialValues, setInitialValues] = useState<FormValuesType>(
    {} as FormValuesType
  );
  const [initialCertifications, setInitialCertifications] = useState<
    CertificationType[]
  >([]);
  const [initialSpecialties, setInitialSpecialties] = useState<SpecialtyType[]>(
    []
  );

  const { loading, error, data } = useQuery(STAFFING_REQUEST_TEMPLATE_QUERY);

  const {
    data: roleLevelsData,
    loading: roleLevelsLoading,
    error: roleLevelsError,
  } = useQuery(ENUM_QUERY, {
    variables: { key: 'RoleLevels' },
  });

  const {
    loading: loadingEdit,
    error: errorEdit,
    data: dataEdit,
  } = useQuery(GET_STAFFING_REQUEST_TEMPLATE_QUERY, {
    skip: !staffingRequestTemplateId,
    variables: { id: staffingRequestTemplateId },
  });

  const {
    loading: staffingRequestTypesLoading,
    data: staffingRequestTypeData,
    error: staffingRequestTypeError,
  } = useQuery(ENUM_QUERY, {
    variables: { key: 'StaffingRequestTypes' },
  });

  const templateData = dataEdit ? dataEdit.staffingRequestTemplate : null;

  const currentEntity = useCurrentEntity();

  const billingConfigRequired = (currentEntity.worksProgramsCount || 0) > 0;

  useEffect(() => {
    if (templateData) {
      const values: FormValuesType = {
        staffingRequestType: templateData.staffingRequestType,
        name: templateData.name,
        organizationBusinessId:
          templateData.organizationUnit.department.organizationBusiness.id,
        organizationUnitId: templateData.organizationUnit.id,
        costCenter: templateData.organizationUnit.costCenter,
        roleId: templateData.role.id,
        roleLevel: templateData.roleLevel,
        billingRegularRate: templateData.billingRegularRate,
        worksListTemplateId: templateData.worksListTemplate.id,
        externalId: templateData.externalId || '',
        worksBillingConfigId: templateData.worksBillingConfig?.id,
        yearsOfExperience: templateData.yearsOfExperience || undefined,
        technologyIds: templateData.staffingRequestTemplateTechnologies.map(
          (technology: { technology: { id: string } }) =>
            technology.technology.id
        ),
        description: templateData.description,
      };

      const formattedSpecialties = formatSelectedSpecialties(
        templateData.staffingRequestTemplateSpecialties
      );
      const formattedCertifications = formatSelectedCertifications(
        templateData.staffingRequestTemplateCertifications
      );

      setSelectedSpecialties(formattedSpecialties);
      setSelectedCertifications(formattedCertifications);
      setInitialSpecialties(cloneDeep(formattedSpecialties));
      setInitialCertifications(cloneDeep(formattedCertifications));

      setName(values.name);

      setInitialValues(values);

      form.setFieldsValue(values);
    }
  }, [form, templateData]);

  const handleDisableButton = useCallback(() => {
    const currentValues = form.getFieldsValue(true);
    delete currentValues['certifications'];

    if (
      isEdit &&
      isEqual(currentValues, initialValues) &&
      isEqual(selectedCertifications, initialCertifications) &&
      isEqual(selectedSpecialties, initialSpecialties)
    ) {
      setDisableSubmitButton(true);
    } else {
      setDisableSubmitButton(false);
    }
  }, [
    form,
    initialCertifications,
    initialSpecialties,
    initialValues,
    isEdit,
    selectedCertifications,
    selectedSpecialties,
  ]);

  useEffect(() => {
    handleDisableButton();
  }, [selectedCertifications, selectedSpecialties, handleDisableButton]);

  if (
    loading ||
    loadingEdit ||
    staffingRequestTypesLoading ||
    roleLevelsLoading
  ) {
    return <LoadingState />;
  }

  const anyError =
    error || errorEdit || staffingRequestTypeError || roleLevelsError;
  if (anyError) {
    return <ErrorHandler error={anyError} log="StaffingRequestTemplateForm" />;
  }

  const hasNameTakenError =
    name &&
    data?.staffingRequestTemplates.some(
      (template: { name: string }) => template.name === name
    );

  const handleChange = (
    changedValues: Partial<FormValuesType>,
    allValues: FormValuesType
  ) => {
    const newTemplateName = updateTemplateName(form, allValues, data);

    if (!isEdit) {
      setName(newTemplateName);
    }

    const newCostCenter = findEntityById(
      allValues.organizationUnitId,
      data?.organizationUnits
    )?.costCenter;

    form.setFieldsValue({ costCenter: newCostCenter });
    handleDisableButton();
  };

  const showRoleLevelWarning = () => {
    if (!isEdit && staffingRequestTemplateId && hasNameTakenError) {
      return (
        <Gutter direction="bottom" size="md">
          <Banner type="info">
            Please change the <b>role</b> or <b>role level</b> to create a new
            template
          </Banner>
        </Gutter>
      );
    }
  };

  return (
    <>
      {showRoleLevelWarning()}
      <div>
        <Form
          className="staffing-request-template-form"
          form={form}
          onValuesChange={handleChange}
          onFinish={(values) =>
            handleSubmit(values, selectedSpecialties, selectedCertifications)
          }
          labelCol={{ span: 8 }}
          labelAlign="left"
          initialValues={{
            staffingRequestType: 'long_term',
            roleLevel: 'not_applicable',
          }}
        >
          <div className="PositionDetails">
            <Gutter direction="bottom" size="md">
              <Title size="h3">Position Details</Title>
            </Gutter>
            <Form.Item name="name" label="Template Name" colon={false} required>
              <Input
                size="large"
                disabled
                placeholder="Auto-generated upon completion below"
              />
            </Form.Item>
            <OrganizationBusinessSelect
              disabled={isEdit}
              organizationBusinesses={data?.organizationBusinesses || []}
              form={form}
            />
            <OrganizationUnitSelect
              disabled={isEdit}
              organizationBusinessId={form.getFieldValue(
                'organizationBusinessId'
              )}
            />
            <Form.Item name="costCenter" label="Cost Center" colon={false}>
              <Input
                size="large"
                disabled
                placeholder="Complete the fields above first"
              />
            </Form.Item>
            <Form.Item
              name="staffingRequestType"
              label="Type"
              colon={false}
              required
            >
              <Select
                disabled={isEdit}
                placeholder="Type"
                size="large"
                getPopupContainer={(trigger) => trigger.parentNode}
              >
                {staffingRequestTypeData?.enum?.map(
                  (staffingRequestType: {
                    name: string;
                    description: string;
                  }) => {
                    if (
                      !currentEntity.onDemandEnabled &&
                      staffingRequestType.name === 'on_demand'
                    ) {
                      return <></>;
                    }

                    return (
                      <Select.Option
                        value={staffingRequestType.name}
                        key={staffingRequestType.name}
                      >
                        {capitalize(staffingRequestType.description)}
                      </Select.Option>
                    );
                  }
                )}
              </Select>
            </Form.Item>
            <RoleItem
              disabled={isEdit}
              roles={data?.roles || []}
              roleId={form.getFieldValue('roleId')}
              roleLevel={form.getFieldValue('roleLevel')}
              roleLevels={roleLevelsData?.enum || []}
              hasError={!!hasNameTakenError}
            />
            <Form.Item
              name="billingRegularRate"
              label="Regular Rate (hr)"
              colon={false}
              rules={[{ required: true, message: 'Please add a regular rate' }]}
            >
              <CurrencyInput size="large" placeholder="0.00" />
            </Form.Item>
            <ComplianceListTemplateSelect
              complianceTemplates={data?.worksListTemplates || []}
            />
            <Form.Item label="External Job ID" name="externalId" colon={false}>
              <Input
                size="large"
                placeholder="What's the external job ID number?"
              />
            </Form.Item>
            <BillingConfigSelect
              billingConfigs={data?.worksBillingConfigs || []}
              required={billingConfigRequired}
            />
          </div>
          <Divider gutter />

          <Gutter direction="bottom" size="md">
            <Title size="h3">Experience</Title>
          </Gutter>
          <ClinicalSpecialtiesSelect
            clinicalSpecialties={data?.clinicalSpecialties || []}
            selectedSpecialties={selectedSpecialties}
            setSelectedSpecialties={setSelectedSpecialties}
          />
          <YearsOfExperienceSelect />
          <TechnologiesSelect technologies={data?.technologies || []} />
          <Divider gutter />

          <Gutter direction="bottom" size="md">
            <Title size="h3">Certifications</Title>
          </Gutter>
          <CertificationsSelect
            certifications={data?.certificationList || []}
            selectedCertifications={selectedCertifications}
            setSelectedCertifications={setSelectedCertifications}
          />
          <Divider gutter />

          <Gutter direction="bottom" size="md">
            <Title size="h3">Description</Title>
          </Gutter>
          <Form.Item name="description" colon={false}>
            <Input.TextArea
              rows={4}
              placeholder="Any additional information?"
            />
          </Form.Item>
        </Form>
        <ButtonGroup>
          <Button onClick={onClose}>Cancel</Button>
          <Button
            kind="primary"
            onClick={form.submit}
            disabled={disableSubmitButton}
          >
            {isEdit ? 'Save' : 'Create'} Staffing Request Template
          </Button>
        </ButtonGroup>
      </div>
      <style jsx>
        {`
          :global(.staffing-request-template-form .ant-input-number),
          :global(
            .staffing-request-template-form .ant-form-item-control-input-content
          ),
          :global(.staffing-request-template-form .ant-picker) {
            width: 100%;
          }

          :global(.staffing-request-template-form .hint) {
            margin-top: 8px;
            color: ${Colors.midnightLighter};
          }

          :global(.staffing-request-template-form .ant-alert) {
            margin-bottom: 1.5rem;
          }
          :global(
            .staffing-request-template-form
              .PositionDetails
              .ant-form-item-control
          ) {
            width: auto;
            max-width: 358px;
          }
          :global(.roleItemRow) {
            height: 40px;
          }
          :global(.roleColumn) {
            width: 75%;
          }
          :global(.levelColumn) {
            width: 25%;
          }
        `}
      </style>
    </>
  );
}
